import {
  patchState,
  signalStore,
  withMethods,
  withState,
} from '@ngrx/signals';
import { inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FilegroupService } from './infra/filegroup.service';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { concat, filter, map, mergeMap, of, pipe, Subject, Subscription, switchMap, takeUntil, tap, toArray, Observable } from 'rxjs';
import { HttpErrorResponse, HttpEventType, HttpResponse } from '@angular/common/http';
import { ErrorStore } from '@application/errors/data-access';
import { tapResponse } from '@ngrx/operators';
import {
  setPropLoaded,
  setPropLoading,
  withReqState,
  setPropError,
  setPropInit
} from '@application/data-access';
import { IFile, IUserPhoto, IUserPhotoResponse, IPhotoUploadLinkResponse, IUserPhotoCompleteRequest, IUserPhotoValidateRequest, IPhotoUploadLinkActiveResponse } from './entities/filegroup.interface';
import { PhotoFlags } from './entities/filegroup.enum';
import { DialogStore } from '@shared/components/ui/dialog/data-access';
import { v4 as uuid } from 'uuid';
import { ClientNotificationService, EClientNotificationsEventTypes } from '@shared/push-notification';

export interface IFilegroupStore {
  UserPhotosGroupedById: Record<string, Array<IUserPhotoResponse & { load?: boolean; error?: boolean }>>;
  ActuarProfilePhotoById: Record<string, IUserPhotoResponse>;
  ActuarPhotoByIdState: Record<string, 'loading' | 'loaded' | 'error'>;
  FileState: Record<string, 'loading' | 'loaded' | 'error'>;
  ClearFileState: Record<string, 'loading' | 'loaded' | 'error'>;
  PhotoInMemory: Partial<IUserPhoto>;
  FilesByGroupId: Array<IFile>;
  progressFile: number;
  temporaryPersonId: string;
  photoUploadLink: IPhotoUploadLinkResponse | null;
  photoUploadLinkActive: Record<string, IPhotoUploadLinkActiveResponse | null>;
}

export enum EDeviceAction {
  activate,
  deactivate,
}

/**
 * Builds the cache key used to store a flag-specific profile photo in
 * `ActuarProfilePhotoById` / `ActuarPhotoByIdState`. Keeping it distinct from
 * the plain `personId` key prevents a flag lookup from colliding with the
 * default profile photo of the same person.
 */
export const profilePhotoByFlagKey = (id: string, flag: PhotoFlags): string => `${id}::flag-${flag}`;

export const filegroupInitialState: IFilegroupStore = {
  UserPhotosGroupedById: {},
  ActuarProfilePhotoById: {},
  ActuarPhotoByIdState: {},
  FileState: {},
  ClearFileState: {},
  PhotoInMemory: null,
  FilesByGroupId: null,
  progressFile: 0,
  temporaryPersonId: null,
  photoUploadLink: null,
  photoUploadLinkActive: {},
};


export const FilegroupStore = signalStore(
  { providedIn: 'root' },
  withState<IFilegroupStore>(filegroupInitialState),
  withReqState('getUserPhotosById', 'getFilesByGroupId', 'setFlagUserPhotos', 'clearFlagUserPhotos', 'deleteUserPhotos', 'updateUserPhotos', 'getActuarProfilePhoto', 'getActuarProfilePhotoAsync', 'actuarProfilePhotoById', 'switchDevices', 'deleteFile', 'createFile', 'updateFile', 'saveTemporaryPersonPhotos', 'createPhotoUploadLink', 'completeUserPhoto', 'validateUserPhotoUpload', 'checkPhotoUploadLinkActive'),
  withMethods(
    (
      state,
      filegroupService = inject(FilegroupService),
      errorStore = inject(ErrorStore),
      dialogStore = inject(DialogStore),
      clientNotificationService = inject(ClientNotificationService),
      destroyRef = inject(DestroyRef)
    ) => {
      const checkFlag = (flag: PhotoFlags, valueFlagSum: number): boolean => {
        let isFlag = false;
        if (flag === valueFlagSum) isFlag = true;
        const sum = Object.keys(PhotoFlags).reduce((acc, el) => {
          const key = el as keyof typeof PhotoFlags;
          // `Object.keys` de um enum numérico traz também o mapa reverso ("1" →
          // 'ActuarProfile'); só as chaves de nome têm valor numérico.
          if (typeof PhotoFlags[key] !== 'number' || PhotoFlags[key] === flag) return acc;
          if ((PhotoFlags[key]) + flag === valueFlagSum) isFlag = true;
          return acc + PhotoFlags[key];
        }, 0);
        if (sum + flag === valueFlagSum) isFlag = true;
        return isFlag;
      };

      const deleteFile = rxMethod<{ fileId: string }>(
        pipe(
          tap(() => patchState(state, { ...setPropLoading('deleteFile') })),
          switchMap(({ fileId }) =>
            filegroupService.deleteFile(fileId).pipe(
              tapResponse({
                next: () => {
                  patchState(state, { FilesByGroupId: state.FilesByGroupId().filter((file) => file.FileId !== fileId) }, setPropLoaded('deleteFile'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(state, { ...setPropError('deleteFile', error) });
                }
              })
            )
          )
        )
      );

      /**
       * Relê as fotos da pessoa no servidor. Não passa pelo `getUserPhotosById`,
       * que só busca quem nunca foi carregado, nem mexe no estado de pedido: a
       * lista troca no lugar, sem esqueleto. `mergeMap` porque duas pessoas
       * podem estar a ser escutadas ao mesmo tempo, e a recarga de uma não pode
       * cancelar a da outra.
       */
      const refreshUserPhotos = rxMethod<string>(
        pipe(
          filter((personId) => !!personId && personId !== state.temporaryPersonId()),
          mergeMap((personId) =>
            filegroupService.getUserPhotos(personId).pipe(
              tapResponse({
                next: (response) => {
                  patchState(state, { UserPhotosGroupedById: { ...state.UserPhotosGroupedById(), [personId]: response || null } });
                },
                error: (error: HttpErrorResponse) => errorStore.setHttpError(error),
              })
            )
          )
        )
      );

      /**
       * Depois de um upload pelo link, o link que estava na tela já foi usado:
       * descarta-o e volta a perguntar se há outro válido. Verificação de fundo
       * — atualiza só o `photoUploadLinkActive`, sem o estado de pedido do
       * `checkPhotoUploadLinkActive`; se falhar, o link fica como estava e a
       * foto já entrou na lista.
       */
      const refreshPhotoUploadLink = (personId: string): void => {
        patchState(state, { photoUploadLink: null });

        filegroupService.checkPhotoUploadLinkActive(personId).pipe(
          takeUntilDestroyed(destroyRef),
          tapResponse({
            next: (response) => {
              patchState(state, {
                photoUploadLinkActive: { ...state.photoUploadLinkActive(), [personId]: response },
              });
            },
            error: () => undefined,
          })
        ).subscribe();
      };

      /**
       * Escutas de upload de foto abertas, por pessoa, e quantos componentes
       * pediram cada uma. O avatar do perfil aparece em duas posições (móvel e
       * desktop) e em duas telas (perfil e minha conta), e duas instâncias podem
       * coexistir por um instante ao trocar de posição: a escuta só fecha quando
       * a última a larga.
       */
      const photoUploadListeners = new Map<string, { listeners: number; subscription: Subscription }>();

      const clearTemporaryPersonId = (id: string = state.temporaryPersonId()) => {
        if (!id) return;

        const temporaryUserPhotos = structuredClone(state.UserPhotosGroupedById()[id]) || [];
        const FileState = structuredClone(state.FileState());
        const ClearFileState = structuredClone(state.ClearFileState());
        for (const photo of temporaryUserPhotos) {
           delete FileState[photo.FileId];
           delete ClearFileState[photo.FileId];
        }
        patchState(state, { 
          ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [id]: null },
          UserPhotosGroupedById: { ...state.UserPhotosGroupedById(), [id]: null },
          FileState,
          ClearFileState,
          temporaryPersonId: null,
        });
      }

      const requestByFlag = (groupId: string, fileId: string, flag: PhotoFlags, flagsToSet: number, currentFlags: number) => {
        const isFlagToSet = checkFlag(flag, flagsToSet);
        const isCurrentFlag = checkFlag(flag, currentFlags);

        const payload = { groupId, fileId, flag };
        if (isFlagToSet && !isCurrentFlag) {
          return filegroupService.setFlagUserPhotos(payload);
        }
        if (!isFlagToSet && isCurrentFlag && flag === PhotoFlags.Facial) {
          return filegroupService.clearFlagUserPhotos(payload);
        }
        return of()
      }

      return {
        checkFlag,
        getUserPhotosById: rxMethod<{ id: string }>(
          pipe(
            filter(({ id }) => !!id && state.UserPhotosGroupedById()[id] === undefined && id !== state.temporaryPersonId()),
            tap(() => patchState(state, { ...setPropLoading('getUserPhotosById') })),
            switchMap(({ id }) =>
              filegroupService.getUserPhotos(id).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, { UserPhotosGroupedById: { ...state.UserPhotosGroupedById(), [id]: response || null } }, setPropLoaded('getUserPhotosById'));
                  },
                  error: (error: HttpErrorResponse) => errorStore.setHttpError(error),
                })
              )
            )
          )
        ),
        setFlagUserPhotos: rxMethod<{ groupId: string; fileId: string; flag: PhotoFlags; }>(pipe(
          tap(({ fileId }) => patchState(state, { ...setPropLoading('setFlagUserPhotos'), FileState: { ...state.FileState(), [fileId]: 'loading' } })),
          switchMap(({ groupId, fileId, flag }) => {
            const request$ = groupId ? filegroupService.setFlagUserPhotos({ fileId, flag }) : of(null);
            return request$.pipe(
              tapResponse({
                next: () => {
                  const photos = structuredClone(state.UserPhotosGroupedById()[groupId || state.temporaryPersonId()]);

                  photos.forEach((photo) => {
                    if (checkFlag(flag, photo.Flags) && PhotoFlags.Facial !== flag) photo.Flags = photo.Flags - flag;
                    if (photo.FileId === fileId) photo.Flags = photo.Flags + flag;
                    if (checkFlag(PhotoFlags.ActuarProfile, photo.Flags) && flag === PhotoFlags.ActuarProfile) patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [groupId || state.temporaryPersonId()]: photo } });
                  });

                  const userPhotos = photos;
                  patchState(state, { FileState: { ...state.FileState(), [fileId]: 'loaded' }, UserPhotosGroupedById: { ...state.UserPhotosGroupedById(), [groupId || state.temporaryPersonId()]: userPhotos } }, setPropLoaded('setFlagUserPhotos'));
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.handleError({ error, title: flag === PhotoFlags.Facial ? 'FILEGROUP.SET_FACIAL_ERROR_TITLE' : undefined });
                  patchState(state, { FileState: { ...state.FileState(), [fileId]: 'error' }, ...setPropError('setFlagUserPhotos', error) });
                },
              })
            )
          })
        )),
        clearFlagUserPhotos: rxMethod<{ groupId: string; fileId: string; flag: PhotoFlags; }>(pipe(
          tap(({ fileId }) => patchState(state, { ...setPropLoading('clearFlagUserPhotos'), ClearFileState: { ...state.ClearFileState(), [fileId]: 'loading' } })),
          switchMap(({ groupId, fileId, flag }) => {
            const request$ = groupId ? filegroupService.clearFlagUserPhotos({ fileId, flag }) : of(null);
            return request$.pipe(
              tapResponse({
                next: () => {
                  const userPhotos = structuredClone(state.UserPhotosGroupedById()[groupId || state.temporaryPersonId()]);

                  userPhotos.forEach((photo) => {
                    if (photo.FileId === fileId) photo.Flags = photo.Flags - flag;
                  });
                  patchState(state, { ClearFileState: { ...state.ClearFileState(), [fileId]: 'loaded' }, UserPhotosGroupedById: { ...state.UserPhotosGroupedById(), [groupId || state.temporaryPersonId()]: userPhotos } }, setPropLoaded('clearFlagUserPhotos'));
                },
                error: (error: HttpErrorResponse) => {
                  errorStore.setHttpError(error)
                  patchState(state, { ClearFileState: { ...state.ClearFileState(), [fileId]: 'error' }, ...setPropError('clearFlagUserPhotos', error) });
                }
              })
            )
          })
        )),
        deleteUserPhotos: rxMethod<{ groupId: string; fileId: string; }>(pipe(
          tap(() => patchState(state, { ...setPropLoading('deleteUserPhotos') })),
            switchMap(({ groupId, fileId }) => {
              const request$ = groupId ? filegroupService.deleteUserPhotos(fileId) : of(null);
              return request$.pipe(
                tapResponse({
                  next: () => {
                    const userPhotos = state.UserPhotosGroupedById()[groupId || state.temporaryPersonId()].filter((photo) => {
                      const isExclude = photo.FileId === fileId;
                      const userPhoto = isExclude ? photo : null;
                      if (userPhoto && checkFlag(PhotoFlags.ActuarProfile, userPhoto.Flags)) patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [groupId || state.temporaryPersonId()]: null } }, setPropLoaded('deleteUserPhotos'));

                      return !isExclude;
                    });
                    patchState(state, { UserPhotosGroupedById: { ...state.UserPhotosGroupedById(), [groupId || state.temporaryPersonId()]: userPhotos } }, setPropLoaded('deleteUserPhotos'));
                  },
                  error: (error: HttpErrorResponse) => errorStore.setHttpError(error),
                })
              )
            })
          )
        ),
        updateUserPhotos: rxMethod<Partial<IUserPhoto>>(pipe(
          tap(() => patchState(state, { ...setPropLoading('updateUserPhotos') })),
          switchMap(({ PersonId, File }) => {
            const request$ = PersonId ? filegroupService.createUserPhotos({ PersonId, File }) : of({ File, FileId: uuid(), Flags: 0 } as IUserPhotoResponse);
            return request$.pipe(
              tapResponse({
                next: (userPhoto) => {
                  const userPhotos = structuredClone(state.UserPhotosGroupedById()[PersonId || state.temporaryPersonId()]) || [];
                  if (!PersonId && !userPhoto.Flags && !userPhotos.length) userPhoto.Flags = PhotoFlags.ActuarProfile + PhotoFlags.Facial + PhotoFlags.AppProfile;
                  userPhotos.unshift(userPhoto);

                  if (checkFlag(PhotoFlags.ActuarProfile, userPhoto.Flags)) patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [PersonId || state.temporaryPersonId()]: userPhoto } });

                  patchState(state, { UserPhotosGroupedById: { ...state.UserPhotosGroupedById(), [PersonId || state.temporaryPersonId()]: userPhotos } }, setPropLoaded('updateUserPhotos'));
                },
                error: (error: HttpErrorResponse) => {
                  patchState(state, { ...setPropError('updateUserPhotos', error) });
                  errorStore.setHttpError(error)
                },
              })
            )
          })
        )),
        getActuarProfilePhoto: rxMethod<{ id: string; cancelToken?: DestroyRef }>(
          pipe(
            filter(({ id }) => !!id && state.ActuarProfilePhotoById()[id] === undefined && id !== state.temporaryPersonId()),
            tap(({ id }) => patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [id]: null }, ActuarPhotoByIdState: { ...state.ActuarPhotoByIdState(), [id]: 'loading' } })),
            mergeMap(({ id, cancelToken }) => {
              const request$ = filegroupService.getActuarProfilePhoto(id).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [id]: response[0] || null }, ActuarPhotoByIdState: { ...state.ActuarPhotoByIdState(), [id]: 'loaded' } });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById()}, ActuarPhotoByIdState: { ...state.ActuarPhotoByIdState(), [id]: 'error' } });
                  },
                })
              );
              return cancelToken ? request$.pipe(takeUntilDestroyed(cancelToken)) : request$;
            })
          )
        ),
        getProfilePhotoByFlag: rxMethod<{ id: string; flag: PhotoFlags; cancelToken?: DestroyRef }>(
          pipe(
            filter(({ id, flag }) => !!id && !(profilePhotoByFlagKey(id, flag) in state.ActuarProfilePhotoById()) && id !== state.temporaryPersonId()),
            tap(({ id, flag }) => {
              const key = profilePhotoByFlagKey(id, flag);
              patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [key]: null }, ActuarPhotoByIdState: { ...state.ActuarPhotoByIdState(), [key]: 'loading' } });
            }),
            mergeMap(({ id, flag, cancelToken }) => {
              const key = profilePhotoByFlagKey(id, flag);
              const request$ = filegroupService.getUserPhotos(id, flag).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [key]: response[0] || null }, ActuarPhotoByIdState: { ...state.ActuarPhotoByIdState(), [key]: 'loaded' } });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById()}, ActuarPhotoByIdState: { ...state.ActuarPhotoByIdState(), [key]: 'error' } });
                  },
                })
              );
              return cancelToken ? request$.pipe(takeUntilDestroyed(cancelToken)) : request$;
            })
          )
        ),
        getActuarProfilePhotoAsync: rxMethod<{ id: string; cancelToken?: DestroyRef }>(
          pipe(
            filter(({ id }) => !!id && state.ActuarProfilePhotoById()[id] === undefined && id !== state.temporaryPersonId()),
            tap(({ id }) => patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [id]: null }, ActuarPhotoByIdState: { ...state.ActuarPhotoByIdState(), [id]: 'loading' } })),
            mergeMap(({ id, cancelToken }) => {
              const request$ = filegroupService.getActuarProfileAsync(id).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [id]: response[0] || null }, ActuarPhotoByIdState: { ...state.ActuarPhotoByIdState(), [id]: 'loaded' } });
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(state, { ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById()}, ActuarPhotoByIdState: { ...state.ActuarPhotoByIdState(), [id]: 'error' } });
                  },
                })
              );
              return cancelToken ? request$.pipe(takeUntilDestroyed(cancelToken)) : request$;
            })
          )
        ),
        switchDevices: rxMethod<{ deviceName: string, action: EDeviceAction }>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('switchDevices') })),
            switchMap(({ deviceName, action }) =>
              filegroupService.switchDevices(deviceName, action).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, { UserPhotosGroupedById: { ...state.UserPhotosGroupedById(), [deviceName]: response ?? null } }, setPropLoaded('switchDevices'));
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, { ...setPropError('switchDevices', error) });
                  }
                })
              )
            )
          )
        ),
        getFilesByGroupId: rxMethod<{ groupId: string; folder: number }>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('getFilesByGroupId') })),
            switchMap(({ groupId, folder }) =>
              filegroupService.getFilesByGroupId(groupId, folder).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, { FilesByGroupId: response }, setPropLoaded('getFilesByGroupId'));
                  },
                  error: (error: HttpErrorResponse) => {
                    errorStore.setHttpError(error);
                    patchState(state, { ...setPropError('getFilesByGroupId', error) });
                  }
                })
              )
            )
          )
        ),
        deleteFile,
        openConfirmDeleteFile: (fileId: string) => {
          dialogStore.openDialog({
            title: 'FILEGROUP.DELETE_CONFIRM.TITLE',
            message: 'FILEGROUP.DELETE_CONFIRM.MESSAGE',
            closeOnSignal: [state.deleteFileLoaded, state.deleteFileError],
            primaryButton: {
              label: 'COMPONENTS.BUTTON.CANCEL',
              action: () => {
                dialogStore.closeDialog();
              }
            },
            secondaryButton: {
              label: 'COMPONENTS.BUTTON.YES_DELETE',
              type: 'destructive',
              loadingOnsignal: state.deleteFileLoading,
              action: () => {
                deleteFile({ fileId });
              }
            }
          })
        },
        createFile: rxMethod<{ file: Partial<IFile>; cancelRequest: Subject<void> }>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('createFile'), progressFile: 0 })),
            switchMap(({ file, cancelRequest }) =>
              filegroupService.createFile(file).pipe(
                takeUntil(cancelRequest),
                tapResponse({
                  next: (response) => {
                    if (response.type === HttpEventType.UploadProgress) {
                      const progress = Math.round(100 * (response.loaded / (response.total ?? 1)));
                      patchState(state, { progressFile: progress });
                      return;
                    }
                    if (response instanceof HttpResponse) {
                      const files = state.FilesByGroupId() || [];
                      patchState(state, { FilesByGroupId: [response.body, ...files], ...setPropLoaded('createFile') });
                    }
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, { ...setPropError('createFile', error) });
                  }
                })
              )
            )
          )
        ),
        updateFile: rxMethod<{ file: Partial<IFile>; cancelRequest: Subject<void> }>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('updateFile') })),
            switchMap(({ file, cancelRequest }) =>
              filegroupService.updateFile(file).pipe(
                takeUntil(cancelRequest),
                tapResponse({
                  next: (response) => {
                    if (response.type === HttpEventType.UploadProgress) {
                      const progress = Math.round(100 * (response.loaded / (response.total ?? 1)));
                      patchState(state, { progressFile: progress });
                      return;
                    }
                    if (response instanceof HttpResponse) {
                      const FilesByGroupId = (state.FilesByGroupId() ?? []).map((file) => file.FileId === response.body.FileId ? response.body : file);
                      patchState(state, { FilesByGroupId }, setPropLoaded('updateFile'));
                    }
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, { ...setPropError('updateFile', error) });
                  }
                })
              )
            )
          )
        ),
        initProps: () => {
          patchState(state, {
            ...setPropInit(
              'getUserPhotosById',
              'getFilesByGroupId',
              'setFlagUserPhotos',
              'clearFlagUserPhotos',
              'deleteUserPhotos',
              'updateUserPhotos',
              'getActuarProfilePhoto',
              'getActuarProfilePhotoAsync',
              'actuarProfilePhotoById',
              'switchDevices',
              'deleteFile',
              'createFile',
              'updateFile',
              'saveTemporaryPersonPhotos',
              'completeUserPhoto',
              'validateUserPhotoUpload',
              'checkPhotoUploadLinkActive'
            )
          })
        },
        clearTemporaryPersonId,
        saveTemporaryPersonPhotos: rxMethod<string>(pipe(
          tap(() => patchState(state, { ...setPropLoading('saveTemporaryPersonPhotos') })),
          switchMap((PersonId) => {
            const temporaryUserPhotos = structuredClone(state.UserPhotosGroupedById()[state.temporaryPersonId()]) || [];
            clearTemporaryPersonId();
            const flagsToSetByIndex: Record<number, number> = {}
            const requests$ = temporaryUserPhotos.reverse().map((photo, index) => {
              flagsToSetByIndex[index] = photo.Flags;
              return filegroupService.createUserPhotos({
                PersonId,
                File: {
                  FileName: photo?.File?.FileName,
                  StorageObjectId: photo?.File?.StorageObjectId,
                  Url: photo?.File?.Url
                },
              })
            })
            return concat(...requests$).pipe(
              toArray(),
              map((userPhotos) => ({ userPhotos, flagsToSetByIndex, PersonId }))
            )
          }),
          switchMap(({ flagsToSetByIndex, PersonId, userPhotos}) => {
            const requests$ = userPhotos.map((photo, index) => {
                const flagsToSet =  flagsToSetByIndex[index];
                const currentFlags = photo.Flags
                photo.Flags = flagsToSet;
                return Object.values(PhotoFlags).filter(flag => typeof flag === 'number').map((flag) => requestByFlag(PersonId, photo.FileId, flag, flagsToSet, currentFlags))
              }).flat()
            return concat(...requests$).pipe(
              toArray(),
              map(() => ({ userPhotos, PersonId }))
            )
          }),
          tapResponse({
            next: ({ PersonId, userPhotos }) => {
              const actuarProfile = userPhotos.find((photo) => checkFlag(PhotoFlags.ActuarProfile, photo.Flags));

              patchState(state, {
                UserPhotosGroupedById: { ...state.UserPhotosGroupedById(), [PersonId]: userPhotos.reverse() },
                ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [PersonId]: actuarProfile || null },
                ...setPropLoaded('saveTemporaryPersonPhotos')
              })
              
            },
            error: (error: HttpErrorResponse) => {
              patchState(state, { ...setPropError('saveTemporaryPersonPhotos', error) });
              errorStore.setHttpError(error)
            },
          })
        )),
        setTemporaryPersonId: (temporaryPersonId: string) => {
          patchState(state, { temporaryPersonId });
        },
        createPhotoUploadLink: rxMethod<string>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('createPhotoUploadLink'), photoUploadLink: null })),
            switchMap((personId) =>
              filegroupService.createPhotoUploadLink(personId).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, { photoUploadLink: response, ...setPropLoaded('createPhotoUploadLink') });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, { ...setPropError('createPhotoUploadLink', error) });
                    errorStore.setHttpError(error);
                  }
                })
              )
            )
          )
        ),
        setPhotoUploadLink: (photoUploadLink: IPhotoUploadLinkResponse | null) => {
          patchState(state, { photoUploadLink });
        },
        completeUserPhoto: rxMethod<IUserPhotoCompleteRequest>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('completeUserPhoto') })),
            switchMap((payload) =>
              filegroupService.completeUserPhoto(payload).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(state, { ...setPropLoaded('completeUserPhoto') });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, { ...setPropError('completeUserPhoto', error) });
                    errorStore.setHttpError(error);
                  }
                })
              )
            )
          )
        ),
        validateUserPhotoUpload: rxMethod<IUserPhotoValidateRequest>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('validateUserPhotoUpload') })),
            switchMap((payload) =>
              filegroupService.validateUserPhotoUpload(payload).pipe(
                tapResponse({
                  next: () => {
                    patchState(state, { ...setPropLoaded('validateUserPhotoUpload') });
                  },
                  error: (error: HttpErrorResponse) => {
                    patchState(state, { ...setPropError('validateUserPhotoUpload', error) });
                    errorStore.setHttpError(error);
                  }
                })
              )
            )
          )
        ),
        checkPhotoUploadLinkActive: rxMethod<string>(
          pipe(
            tap(() => patchState(state, { ...setPropLoading('checkPhotoUploadLinkActive') })),
            switchMap((personId) =>
              filegroupService.checkPhotoUploadLinkActive(personId).pipe(
                tapResponse({
                  next: (response) => {
                    const currentActive = state.photoUploadLinkActive();
                    const updatedActive: Record<string, IPhotoUploadLinkActiveResponse | null> = {
                      ...currentActive,
                      [personId]: response
                    };
                    
                    patchState(state, { 
                      photoUploadLinkActive: updatedActive,
                      ...setPropLoaded('checkPhotoUploadLinkActive') 
                    });
                    
                    if (response?.HasValid && response?.UploadId && response?.UploadToken) {
                      patchState(state, {
                        photoUploadLink: {
                          UploadId: response.UploadId,
                          UploadToken: response.UploadToken,
                          PersonId: personId
                        }
                      });
                    }
                  },
                  error: (error: HttpErrorResponse) => {
                    const currentActive = state.photoUploadLinkActive();
                    const updatedActive: Record<string, IPhotoUploadLinkActiveResponse | null> = {
                      ...currentActive,
                      [personId]: null
                    };
                    
                    patchState(state, { 
                      photoUploadLinkActive: updatedActive,
                      ...setPropError('checkPhotoUploadLinkActive', error) 
                    });
                    errorStore.setHttpError(error);
                  }
                })
              )
            )
          )
        ),
        updatePhotoInMemory: (photo: Partial<IUserPhoto>) => {
          patchState(state, { PhotoInMemory: photo });
        },
        /**
         * Writes a flag-specific profile photo straight into the cache the avatar
         * reads (`ActuarProfilePhotoById` under {@link profilePhotoByFlagKey}), so
         * a just-uploaded photo shows without a re-fetch. Pass a cache-busted
         * `File.Url` when the storage URL may be reused. `null` clears the slot.
         */
        setProfilePhotoByFlag: (params: {
          id: string;
          flag: PhotoFlags;
          photo: IUserPhotoResponse | null;
        }): void => {
          if (!params.id) return;
          const key = profilePhotoByFlagKey(params.id, params.flag);
          patchState(state, {
            ActuarProfilePhotoById: { ...state.ActuarProfilePhotoById(), [key]: params.photo },
            ActuarPhotoByIdState: { ...state.ActuarPhotoByIdState(), [key]: 'loaded' },
          });
        },
        /**
         * Escuta o upload de foto da pessoa pelo link (FacialID) e, quando ele
         * chega, relê a lista do servidor — com as flags que ficaram de facto;
         * a foto recusada por ter mais de uma pessoa vem sem a de FacialID.
         *
         * Também relê quando a conexão volta de uma queda, porque o SignalR não
         * reenvia o que foi emitido enquanto ela estava em baixo. E, se a lista
         * já estava em cache quando a escuta abriu, relê na abertura: um upload
         * cujo evento se perdeu com o perfil fechado ficava de fora até alguém
         * recarregar a página.
         *
         * Cada chamada precisa do seu {@link stopPhotoUploadNotifications}.
         */
        listenPhotoUploadNotifications: (personId: string): void => {
          if (!personId) return;

          const current = photoUploadListeners.get(personId);
          if (current) {
            photoUploadListeners.set(personId, { ...current, listeners: current.listeners + 1 });
            return;
          }

          // Sem cache, o `getUserPhotosById` acabou de pedir a lista e o
          // primeiro `onConnected` não tem nada a acrescentar; os seguintes vêm
          // de uma reabertura depois de a conexão fechar. `onConnectionReconnected`
          // é sempre volta de uma queda, mesmo que chegue antes de qualquer
          // `onConnected` (a escuta pode abrir com a conexão partilhada já a
          // reconectar).
          let refreshOnConnect = state.UserPhotosGroupedById()[personId] !== undefined;

          const subscription = clientNotificationService
            .openConnection([EClientNotificationsEventTypes.onUserPhotoUploadedNotification])
            .pipe(takeUntilDestroyed(destroyRef))
            .subscribe(({ eventName, eventData }) => {
              // `onConnected` e `onConnectionReconnected` são eventos da própria
              // conexão, emitidos pelo `ClientNotificationService`, não do hub.
              if (eventName === 'onConnectionReconnected' || (eventName === 'onConnected' && refreshOnConnect)) {
                refreshUserPhotos(personId);
              }
              if (eventName === 'onConnected' || eventName === 'onConnectionReconnected') {
                refreshOnConnect = true;
                return;
              }

              if (
                eventName === EClientNotificationsEventTypes.onUserPhotoUploadedNotification &&
                eventData?.PersonId === personId
              ) {
                refreshUserPhotos(personId);
                refreshPhotoUploadLink(personId);
              }
            });

          photoUploadListeners.set(personId, { listeners: 1, subscription });
        },
        stopPhotoUploadNotifications: (personId: string): void => {
          const current = photoUploadListeners.get(personId);
          if (!current) return;

          if (current.listeners > 1) {
            photoUploadListeners.set(personId, { ...current, listeners: current.listeners - 1 });
            return;
          }

          photoUploadListeners.delete(personId);
          current.subscription.unsubscribe();
        },
      };
    }
  ),
);
